import { useState } from 'react';
import { Mail, Phone, MapPin, Calendar, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

/**
 * Page Contact avec formulaire + intégration Cal.com pour démos
 * Solution gratuite et conforme RGPD
 */
export default function Contact() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: 'general',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const subjects = [
    { value: 'general', label: 'Question générale' },
    { value: 'demo', label: 'Demander une démo' },
    { value: 'pricing', label: 'Informations tarifaires' },
    { value: 'technical', label: 'Support technique' },
    { value: 'partnership', label: 'Partenariat' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: `[${subjects.find(s => s.value === formData.subject)?.label}] ${formData.company || 'Contact'}`,
          message: `Société: ${formData.company || 'Non renseigné'}\nTéléphone: ${formData.phone || 'Non renseigné'}\n\n${formData.message}`
        })
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi');
      }

      setSubmitted(true);
      toast.success('Message envoyé avec succès !');

      // Réinitialiser le formulaire
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        subject: 'general',
        message: ''
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Ouvrir Cal.com pour réserver une démo
  const openCalendly = () => {
    window.open('https://cal.com/orbloom/demo-30-min', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900 flex items-center mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Retour
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-full p-4">
              <Mail className="w-12 h-12 text-white" />
            </div>
          </div>

          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Notre équipe est à votre écoute pour répondre à toutes vos questions
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Coordonnées */}
          <div className="lg:col-span-1 space-y-6">
            {/* Contact Info Cards */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Informations de contact</h3>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="bg-blue-100 rounded-lg p-3 mr-4">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <a href="mailto:contact@orbloom.fr" className="text-gray-900 hover:text-blue-600">
                      contact@orbloom.fr
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-green-100 rounded-lg p-3 mr-4">
                    <Phone className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Téléphone</p>
                    <a href="tel:+33123456789" className="text-gray-900 hover:text-blue-600">
                      +33 1 23 45 67 89
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-purple-100 rounded-lg p-3 mr-4">
                    <MapPin className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Adresse</p>
                    <p className="text-gray-900">
                      123 Avenue de la Formation<br />
                      75001 Paris, France
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Démo */}
            <div className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
              <Calendar className="w-12 h-12 mb-4" />
              <h3 className="text-2xl font-bold mb-2">Demander une démo</h3>
              <p className="text-blue-100 mb-6">
                Réservez un créneau de 30 minutes avec notre équipe pour découvrir Match Compétences
              </p>
              <button
                onClick={openCalendly}
                className="w-full bg-white text-blue-600 py-3 px-6 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-lg flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Réserver une démo
              </button>
              <p className="text-xs text-blue-200 mt-3 text-center">
                Sans engagement • Gratuit • 30 minutes
              </p>
            </div>

            {/* FAQ Link */}
            <div className="bg-gray-100 rounded-2xl p-6">
              <MessageSquare className="w-10 h-10 text-gray-600 mb-3" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Besoin d'aide rapide ?</h3>
              <p className="text-gray-600 text-sm mb-4">
                Consultez notre base de connaissances ou discutez avec notre assistant IA
              </p>
              <button
                onClick={() => navigate('/knowledge-base')}
                className="w-full bg-gray-900 text-white py-2 px-4 rounded-lg hover:bg-gray-800 transition-all text-sm font-medium"
              >
                Voir la FAQ
              </button>
            </div>
          </div>

          {/* Formulaire de contact */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {submitted ? (
                // Message de confirmation
                <div className="text-center py-12">
                  <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Message envoyé !</h3>
                  <p className="text-gray-600 mb-8 max-w-md mx-auto">
                    Merci pour votre message. Notre équipe vous répondra dans les plus brefs délais, généralement sous 24h.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium"
                  >
                    Envoyer un nouveau message
                  </button>
                </div>
              ) : (
                // Formulaire
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Nom */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom complet *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Jean Dupont"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="jean.dupont@exemple.com"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Société */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Société / Organisme
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Votre organisme de formation"
                        />
                      </div>

                      {/* Téléphone */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Téléphone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="+33 1 23 45 67 89"
                        />
                      </div>
                    </div>

                    {/* Sujet */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sujet *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {subjects.map(subject => (
                          <option key={subject.value} value={subject.value}>
                            {subject.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        placeholder="Décrivez votre demande..."
                      />
                    </div>

                    {/* RGPD */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-gray-700">
                        🔒 Vos données sont protégées conformément au RGPD. Elles ne seront utilisées que pour répondre à votre demande et ne seront jamais partagées avec des tiers.
                      </p>
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Envoyer le message
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Horaires */}
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Nos horaires</h3>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Support Email</p>
              <p className="text-lg font-semibold text-gray-900">7j/7, 24h/24</p>
              <p className="text-sm text-gray-600">Réponse sous 24h</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Support Téléphone</p>
              <p className="text-lg font-semibold text-gray-900">Lun-Ven, 9h-18h</p>
              <p className="text-sm text-gray-600">Hors jours fériés</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 mb-2">Démos</p>
              <p className="text-lg font-semibold text-gray-900">Sur rendez-vous</p>
              <p className="text-sm text-gray-600">Flexibilité totale</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
